"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as React from "react";
import ChevronDown from "@/icons/__generated__/ChevronDown/ChevronDown20";
import { cn } from "@/lib/utils";

type AccordionProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Root
>;

const Accordion = AccordionPrimitive.Root;

const defaultChevron = (
  <ChevronDown className="tw-shrink-0 tw-transition-transform tw-duration-200" />
);

type AccordionItemProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Item
> & {
  showDivider?: boolean;
};
const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, showDivider = true, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      {
        "tw-border-b tw-border-b-gray-300": showDivider,
      },
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> & {
  triggerIcon?: React.ReactNode;
};

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ triggerIcon = defaultChevron, className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="tw-flex tw-text-gray-900 tw-m-0">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "tw-flex tw-flex-1 tw-items-center tw-justify-between tw-py-16 tw-font-medium tw-transition-all hover:tw-underline [&[data-state=open]>svg]:tw-rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      {triggerIcon}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, forceMount, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    forceMount={forceMount}
    className={cn("tw-overflow-hidden tw-body-2 tw-text-gray-900", {
      // these are only applied when forceMount is false
      "tw-transition-all data-[state=closed]:tw-animate-accordion-up data-[state=open]:tw-animate-accordion-down":
        !forceMount,
    })}
    {...props}
  >
    {forceMount ? (
      // When forceMount is true, we use a grid-row transition instead of the usual height animation.
      // (radix does not update the --radix-accordion-content-height variable when forceMount is true)
      <div className="tw-grid tw-overflow-hidden tw-transition-[grid-template-rows] tw-duration-200 tw-ease-out [grid-template-rows:1fr] [[data-state=closed]_&]:[grid-template-rows:0fr]">
        <div className="tw-overflow-hidden tw-min-h-0">
          <div className={cn("tw-pb-16", className)}>{children}</div>
        </div>
      </div>
    ) : (
      <div className={cn("tw-pb-16", className)}>{children}</div>
    )}
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  type AccordionItemProps,
  type AccordionProps,
  type AccordionTriggerProps,
};
